import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';

@Directive({
  selector: '[appGermanDate]',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => GermanDateDirective),
      multi: true,
    },
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GermanDateDirective),
      multi: true,
    },
  ],
  standalone: true,
})
export class GermanDateDirective implements Validator, ControlValueAccessor {
  private germanDateRegex: RegExp = /^\d{2}\.\d{2}\.\d{4}$/;
  private isoDateRegex: RegExp = /^\d{4}-\d{2}-\d{2}$/;
  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef) {}

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = this.el.nativeElement.value;
    this.formatAndSetInput(input);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!this.isGermanDate(value) && !this.isISODate(value)) {
      return { invalidDate: true };
    }
    if (this.isGermanDate(value)) {
      control.setValue(this.convertToISODate(value), { emitEvent: false });
    }
    return null;
  }

  writeValue(value: any): void {
    if (value !== undefined) {
      this.formatAndSetInput(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  private formatAndSetInput(value: string): void {
    let formattedInput = value;
    if (this.isISODate(value)) {
      formattedInput = this.formatISODate(value);
    } else if (this.isGermanDate(value)) {
      formattedInput = this.formatDate(value);
    } else {
      formattedInput = this.formatDate(value);
    }
  
    const newValue = this.convertToISODate(formattedInput);
    const currentValue = this.el.nativeElement.value;
  
    if (newValue !== currentValue) {
      this.el.nativeElement.value = formattedInput;
      if (this.onChange) {
        this.onChange(newValue);
      }
    }
  }
  

  private formatDate(value: string): string {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');

    // Format the date as DD.MM.YYYY
    const day = digits.substring(0, 2);
    const month = digits.substring(2, 4);
    const year = digits.substring(4, 8);

    let formattedDate = '';
    if (day) {
      formattedDate += day;
    }
    if (month) {
      formattedDate += '.' + month;
    }
    if (year) {
      formattedDate += '.' + year;
    }

    return formattedDate;
  }

  private convertToISODate(value: string): string {
    const [day, month, year] = value.split('.');
    return `${year}-${month}-${day}`;
  }

  private isISODate(value: string): boolean {
    return this.isoDateRegex.test(value);
  }

  private isGermanDate(value: string): boolean {
    return this.germanDateRegex.test(value);
  }

  private formatISODate(value: string): string {
    const [year, month, day] = value.split('-');
    return `${day}.${month}.${year}`;
  }
}
